const HTML_ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

export default class StringExtensions {
  static toCamelCase(stringObject) {
    return stringObject.charAt(0).toLowerCase() + stringObject.slice(1);
  }

  /**
   * Converts the first character of a string to uppercase and leaves the rest unchanged.
   * This method is useful for formatting strings where the first letter needs to be capitalized.
   *
   * @param {string} stringObject - The string to be converted.
   * @returns {string} The string with the first character capitalized, or the original string if it is empty or falsy.
   *
   * @example
   * StringExtensions.toCapitalizedCase('hello world'); // 'Hello world'
   * StringExtensions.toCapitalizedCase('world'); // 'World'
   * StringExtensions.toCapitalizedCase('communities'); // 'Communities'
   * StringExtensions.toCapitalizedCase('homes'); // 'Homes'
   * StringExtensions.toCapitalizedCase(''); // ''
   * StringExtensions.toCapitalizedCase(null); // null
   */
  static toCapitalizedCase(stringObject) {
    if (!stringObject) {
      return stringObject;
    }

    return stringObject.charAt(0).toUpperCase() + stringObject.slice(1);
  }

  /**
   * Method used to convert a string into kebab case
   * yeah, that's the name :P, you can read more about different type of cases here:
   * https://www.freecodecamp.org/news/snake-case-vs-camel-case-vs-pascal-case-vs-kebab-case-whats-the-difference/
   * @param {string} stringObject
   * @returns a new string in kebab case
   */
  static toKebabCase(stringObject) {
    if (!stringObject){
      return stringObject;
    }

    return stringObject.replace(/[^a-zA-Z0-9]/g, '-');
  }

  /**
 * Converts a camelCase string into kebab-case.
 * This method transforms strings written in camelCase format into kebab-case format,
 * where words are separated by hyphens and all characters are lowercase.
 *
 * @param {string} stringObject - The camelCase string to be converted.
 * @returns {string} The converted string in kebab-case format, or the original string if it is falsy.
 *
 * @example
 * StringExtensions.camelToKebabCase('camelCaseString'); // 'camel-case-string'
 * StringExtensions.camelToKebabCase('anotherExample123'); // 'another-example123'
 * StringExtensions.camelToKebabCase(''); // ''
 * StringExtensions.camelToKebabCase(null); // null
 */
static camelToKebabCase(stringObject) {
  if (!stringObject) {
    return stringObject;
  }

  return stringObject.replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase();
}

  /**
 * Method used to remove special characters from a string
 * @param {string} stringObject
 * @returns a new string without special characters
 * @example
 * StringExtensions.removeSpecialCharacters('Hello!!! World@2024'); // 'Hello World2024'
 * StringExtensions.removeSpecialCharacters('Special #$%^&*() Characters'); // 'Special  Characters'
 */
  static removeSpecialCharacters(stringObject, replace = '') {
    // Replace any non-alphanumeric characters except for spaces and the forward slash ("/")
    // character with an empty string
    if (!stringObject){
      return stringObject;
    }

    return stringObject.replace(/[^\w\s-]+/g, '').replace(/[\s]+/g, replace).replace(/[-]+/g, '-').trim();
  }

  /**
   * Normalizes spaces in a string by replacing multiple spaces with a single space and trimming.
   * @param {string} text - The text to normalize.
   * @returns {string} The normalized text.
   * @example
   * StringExtensions.normalizeSpaces('Hello world !'); // 'Hello world !'
   * StringExtensions.normalizeSpaces('  This   is   a   test.  '); // 'This is a test.'
   */
  static normalizeSpaces(text) {
    if (!text) {
      return '';
    }

    return text.replace(/\s+/g, ' ').trim();
  }

  /**
   * Method used to encode a name string to be used in a URL
   * @param {string} stringObject
   * @returns a new string without special characters, kebab case and lower case
   */
  static encodeNameToUrl(stringObject) {
    if (!stringObject) {
      return '';
    }

    const nameWithoutSpecialCharacters = this.removeSpecialCharacters(stringObject, '-');
    const kebabCase = this.toKebabCase(nameWithoutSpecialCharacters);
    const name = kebabCase.replace("'", '').replace('/', '');
    return name.toLowerCase();
  }

/**
 * The function removes a specified word from a given string and returns the updated string.
 * @param {string} stringObject - A string that contains the word to be removed.
 * @param {string} wordToRemove - The word that needs to be removed from the string.
 * @returns a new string with the specified `wordToRemove` removed from the original `stringObject`.
 */
  static removeWordFromString(stringObject, wordToRemove){
    const startInIndex = stringObject.indexOf(wordToRemove) - 1;
    const newValue = stringObject.substring(0, startInIndex)

    return newValue;
  }

/**
 * Adds a comma seperated value to a string if it does not already contain it.
 * @param existingString - The existing comma seperated value string.
 * @param targetValue - The new value to conditionally add.
 * @returns The new comma seperated value string.
 * @example
 * StringExtensions.addComaSeperatedValue('A,D,GG,Z,K', 'X') // 'A,D,GG,Z,K,X'
 * StringExtensions.addComaSeperatedValue('A,D,GG,Z,K', 'A') // 'A,D,GG,Z,K'
 */
  static addCommaSeperatedValue(existingString, targetValue){
    if(!existingString) {
        return targetValue
    }

  const valueArray = existingString?.split?.(',');

  const hasTargetValue = valueArray?.some(
      (key) => key === targetValue
  );

  return hasTargetValue ? existingString : existingString?.concat(',', targetValue)
};

/**
* Removes a comma seperated value from a string if it contains it.
* @param existingString - The existing comma seperated value string.
* @param targetValue - The new value to conditionally remove.
* @returns The new comma seperated value string.
* @example
* StringExtensions.removeCommaSeperatedValue('A,D,GG,Z,K', 'X') // 'A,D,GG,Z,K'
* StringExtensions.removeCommaSeperatedValue('A,D,GG,Z,K', 'A') // 'D,GG,Z,K'
*/
  static removeCommaSeperatedValue(existingString, targetValue){
    const valueArray = existingString?.split?.(',');

    const newValueArray = valueArray?.filter((value) => value !== targetValue)

    return newValueArray?.join(',');
  };

  /**
   * Escapes a value for safe interpolation into HTML text content or a quoted ("" or '') attribute value.
   * Not safe for unquoted attributes, event-handler attributes (onclick, etc.), URL attributes
   * (href, src), or <script>/<style> contents — those contexts need their own encoding/validation.
   * @param {*} value - The value to escape.
   * @returns {string} The escaped string, or '' if the value is null or undefined.
   * @example
   * StringExtensions.escapeHtml('<b>"A&B"</b>'); // '&lt;b&gt;&quot;A&amp;B&quot;&lt;/b&gt;'
   * StringExtensions.escapeHtml(null); // ''
   */
  static escapeHtml(value) {
    return String(value ?? '').replace(/[&<>"']/g, (ch) => HTML_ESCAPES[ch]);
  }
}
